// ============================================================================
// Protected Route - Requires authentication
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, isLoading: authLoading } = useAuth();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // If user data was passed from AuthCallback, skip verification
    if (location.state?.user) {
      setUser(location.state.user);
      setIsChecking(false);
      // Clear the state to prevent issues on refresh
      window.history.replaceState({}, document.title);
      return;
    }

    // If user is already set from context, we're done
    if (user) {
      setIsChecking(false);
      return;
    }

    // If auth context is still loading, wait
    if (authLoading) {
      return;
    }

    // Server-side verification of session
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setUser(data.data);
            setIsChecking(false);
            return;
          }
        }

        // Not authenticated - redirect to login
        navigate('/login', { replace: true });
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login', { replace: true });
      }
    };

    checkAuth();
  }, [user, authLoading, location.state, navigate, setUser]);

  // Show loading while checking auth
  if (isChecking || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default ProtectedRoute;
